<template>
  <div id="app">
    <div class="intro-video-screen" :class="{ 'intro-hidden': !showIntroVideo }">
      <video
        ref="introVideo"
        class="intro-video"
        muted
        loop
        playsinline
        @ended="closeIntroVideo"
        @enterpictureinpicture="handleEnterPiP"
        @leavepictureinpicture="handleLeavePiP"
      >
        <source src="@/assets/images/policy/bg.mp4" type="video/mp4">
      </video>
      <div class="intro-actions">
        <button
          v-if="pipSupported"
          class="intro-action intro-pip"
          type="button"
          @click="togglePiP"
        >
          <img src="@/assets/images/policy/play.png" alt="画中画">
        </button>
        <button class="intro-action intro-close" type="button" @click="closeIntroVideo">
          <img src="@/assets/images/policy/close.png" alt="关闭">
        </button>
      </div>
    </div>
    <router-view />
    <button
      v-show="showBackTop"
      class="back-top"
      aria-label="返回顶部"
      @click="scrollTop"
    >↑</button>
  </div>
</template>

<script>
const SUSTAINABILITY_ROUTES = ['Policy', 'Application', 'Contribution'];

export default {
  data() {
    return {
      showBackTop: false,
      lastScrollContainer: null,
      showIntroVideo: false,
      isPiPActive: false,
      pipSupported: typeof document !== 'undefined' && 'pictureInPictureEnabled' in document
    };
  },
  watch: {
    $route: {
      immediate: true,
      handler(to) {
        this.handleRouteChange(to);
      }
    },
    showIntroVideo(val) {
      const video = this.$refs.introVideo;
      if (!video) return;
      if (val) {
        video.currentTime = 0;
        video.muted = false;
        video.defaultMuted = false;
        video.volume = Math.max(video.volume || 0, 0.5);
        const ret = video.play();
        if (ret && typeof ret.then === 'function') {
          ret.catch(() => {
            video.muted = true;
            video.defaultMuted = true;
            video.play().catch(() => {});
          });
        }
      } else if (!this.isPiPActive) {
        video.pause();
      }
    }
  },
  methods: {
    handleRouteChange(to) {
      const trigger = !!this.$root.policyIntroTrigger;
      this.$root.policyIntroTrigger = false;

      const inSection = to && SUSTAINABILITY_ROUTES.includes(to.name);
      if (!inSection) {
        this.showIntroVideo = false;
        this.exitPiPIfNeeded();
        return;
      }
      if (to.name === 'Policy' && trigger && !this.isPiPActive) {
        this.showIntroVideo = true;
      } else {
        this.showIntroVideo = false;
      }
    },
    closeIntroVideo() {
      this.exitPiPIfNeeded();
      this.showIntroVideo = false;
      const video = this.$refs.introVideo;
      if (video) {
        video.pause();
      }
    },
    async togglePiP() {
      const video = this.$refs.introVideo;
      if (!video || !this.pipSupported) return;
      try {
        if (document.pictureInPictureElement) {
          await document.exitPictureInPicture();
          return;
        }
        video.muted = false;
        video.defaultMuted = false;
        video.volume = Math.max(video.volume || 0, 0.5);
        video.play().catch(() => {});
        await video.requestPictureInPicture();
      } catch (e) {
        // PiP can be blocked by browser policy or fail if video isn't ready
      }
    },
    handleEnterPiP() {
      this.isPiPActive = true;
      this.showIntroVideo = false;
      const video = this.$refs.introVideo;
      if (video) {
        video.muted = false;
        if (video.paused) {
          video.play().catch(() => {});
        }
      }
    },
    handleLeavePiP() {
      this.isPiPActive = false;
      this.showIntroVideo = false;
      const video = this.$refs.introVideo;
      if (video) {
        video.pause();
      }
    },
    exitPiPIfNeeded() {
      if (this.pipSupported && document.pictureInPictureElement) {
        document.exitPictureInPicture().catch(() => {});
      }
    },
    handleVisibilityChange() {
      if (!document.hidden) return;
      const video = this.$refs.introVideo;
      if (video) {
        video.muted = true;
        video.pause();
      }
      this.exitPiPIfNeeded();
    },
    scrollTop() {
      // 顶层窗口
      window.scrollTo({ top: 0, behavior: 'smooth' });
      // 最近滚动的容器
      if (this.lastScrollContainer && this.lastScrollContainer.scrollTo) {
        try {
          this.lastScrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (e) {
          this.lastScrollContainer.scrollTop = 0;
        }
      }
      // 兜底：常见的滚动容器
      const candidates = document.querySelectorAll('.history-wrapper, .business-wrapper, .tech-wrapper, .application-wrapper, .policy-wrapper, .contribution-wrapper, [data-scrollable]');
      candidates.forEach(el => {
        if (el && el.scrollTop > 0) {
          try {
            el.scrollTo({ top: 0, behavior: 'smooth' });
          } catch (e) {
            el.scrollTop = 0;
          }
        }
      });
    },
    handleScroll(e) {
      const winTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
      let targetTop = 0;
      if (e && e.target && typeof e.target.scrollTop === 'number') {
        targetTop = e.target.scrollTop;
      }
      this.lastScrollContainer = targetTop > 0 ? e.target : this.lastScrollContainer;
      this.showBackTop = winTop > 200 || targetTop > 200;
    }
  },
  mounted() {
    if (typeof window !== 'undefined') {
      // 监听窗口滚动
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      // 捕获子容器滚动（如各页面内部的 overflow 容器）
      window.addEventListener('scroll', this.handleScroll, { passive: true, capture: true });
      this.handleScroll();
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }
  },
  beforeDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('scroll', this.handleScroll, true);
    }
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }
    this.exitPiPIfNeeded();
  }
};
</script>

<style lang="css" src="./assets/common.css">
</style>

<style scoped lang="scss">
.intro-video-screen {
  position: fixed;
  inset: 0;
  z-index: 9999;
  width: 100vw;
  height: 100vh;
  background: #000;
  overflow: hidden;
  transition: opacity 0.3s ease;
}

.intro-video-screen.intro-hidden {
  opacity: 0;
  pointer-events: none;
}

.intro-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.intro-actions {
  position: absolute;
  top: 32px;
  right: 40px;
  z-index: 1;
  display: flex;
  gap: 12px;
}

.intro-action {
  min-width: 88px;
  height: 40px;
  padding: 0 20px;
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 20px;
  color: #fff;
  background: rgba(0, 0, 0, 0.38);
  font-size: var(--fs-body-sm);
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;
}

.intro-action:hover {
  background: rgba(113, 21, 26, 0.86);
  transform: translateY(-1px);
}

.intro-pip,
.intro-close {
  min-width: auto;
  padding: 6px;
  line-height: 0;
  border: none;
  background: transparent;

  img {
    width: 28px;
    height: 28px;
    display: block;
    object-fit: contain;
  }
}

@media (orientation: portrait) {
  .intro-actions {
    top: 20px;
    right: 20px;
    gap: 8px;
  }

  .intro-action {
    min-width: 76px;
    height: 36px;
    padding: 0 16px;
  }
}

.back-top {
  position: fixed;
  right: 2vw;
  bottom: 6vw;
  width: 35px;
  height: 35px;
  border-radius: 50%;
  border: none;
  background: #71151A;
  color: #fff;
  cursor: pointer;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: opacity 0.2s ease, transform 0.2s ease;
  z-index: 999;
}
.back-top:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.24);
}
.back-top:active {
  transform: translateY(0);
}

@media (max-width: 1440px)  {
  .back-top {
    bottom: 16vw;
  }
}
</style>
